// İzne personel ekle/ata modallarının ortak aday + "yeni oluştur" mantığı (GL6 §4.8). Her uygulama kendi
// sorgusunu ve atama mutasyonunu tutar (anahtarlar/API farklı); bu kanca yalnız listeyi aday kümesine
// çevirir, az önce oluşturulanı listeye katıp seçer ve sheet kapanış odağını çözer.

import { type RefObject, useMemo, useRef, useState } from 'react';
import type { ContractorPersonnelDto, CreatePersonnelResult } from '../api/contractors';
import { isPermitCandidate } from './personnelStatus';

export interface PermitCandidateOptions {
  /** Aday satırlar: yeni oluşturulanlar üstte, taze liste satırı yerel kopyayı ezer. */
  options: ContractorPersonnelDto[];
  selected: Set<string>;
  /** Seçili olup hâlâ `options` içinde görünen kimlikler — gönderilecek küme. */
  visibleSelected: string[];
  toggle: (uuid: string) => void;
  /** Satır onay kutusuna ref callback'i. */
  checkboxRef: (uuid: string) => (el: HTMLInputElement | null) => void;
  /** "Yeni personel oluştur" düğmesine bağlanır (sheet odak yedeği). */
  createTriggerRef: RefObject<HTMLButtonElement | null>;
  /** `PersonnelCreateSheet.restoreFocusRef`: yeni kişinin kutusu, yoksa tetikleyici; kapanışta okunur. */
  sheetRestoreRef: RefObject<HTMLElement | null>;
  /** `PersonnelCreateSheet.onCreated`: yalnız `kind: 'Personnel'` aday olur ve seçilir (D1). */
  handleCreated: (result: CreatePersonnelResult) => void;
  reset: () => void;
}

export function usePermitCandidateOptions(
  rows: ContractorPersonnelDto[] | undefined,
  coveredUuids: string[],
): PermitCandidateOptions {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [justCreated, setJustCreated] = useState<ContractorPersonnelDto[]>([]);
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);
  const checkboxRefs = useRef(new Map<string, HTMLInputElement>());
  const lastCreatedRef = useRef<string | null>(null);
  // Sheet bu ref'i kapanışta OKUR; modal aynı commit'te yeniden mount olur — değer `current` getter'ında çözülür.
  const sheetRestoreRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        const created = lastCreatedRef.current;
        return (
          (created ? (checkboxRefs.current.get(created) ?? null) : null) ?? createTriggerRef.current
        );
      },
    }),
    [],
  );

  const covered = useMemo(() => new Set(coveredUuids), [coveredUuids]);
  const options = useMemo(() => {
    const byUuid = new Map<string, ContractorPersonnelDto>();
    for (const p of justCreated) {
      byUuid.set(p.uuid, p);
    }
    for (const p of rows ?? []) {
      byUuid.set(p.uuid, p);
    }
    return [...byUuid.values()].filter((p) => isPermitCandidate(p) && !covered.has(p.uuid));
  }, [rows, justCreated, covered]);

  const visibleSelected = useMemo(
    () => options.filter((p) => selected.has(p.uuid)).map((p) => p.uuid),
    [options, selected],
  );

  function toggle(uuid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  function checkboxRef(uuid: string) {
    return (el: HTMLInputElement | null) => {
      if (el) {
        checkboxRefs.current.set(uuid, el);
      } else {
        checkboxRefs.current.delete(uuid);
      }
    };
  }

  function handleCreated({ personnel }: CreatePersonnelResult) {
    if (personnel.kind !== 'Personnel' || !isPermitCandidate(personnel)) {
      lastCreatedRef.current = null;
      return;
    }
    lastCreatedRef.current = personnel.uuid;
    setJustCreated((prev) => [...prev, personnel]);
    setSelected((prev) => new Set(prev).add(personnel.uuid));
  }

  function reset() {
    setSelected(new Set());
    setJustCreated([]);
    lastCreatedRef.current = null;
  }

  return {
    options,
    selected,
    visibleSelected,
    toggle,
    checkboxRef,
    createTriggerRef,
    sheetRestoreRef,
    handleCreated,
    reset,
  };
}
